import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { Authorizations } from "~/api/types";
import NoData from "~/components/containers/NoData.tsx";
import { routes } from "~/router";

import { Site } from "../../api/Api";
import { Icon } from "../../assets/icons/Icon";
import Button from "../ui/button/Button";
import { Pagination, PaginationConfig } from "../ui/pagination/Pagination";
import { SearchBar } from "../ui/search/SearchBar";
import { Stats } from "./SiteCard";
import { SiteList } from "./SiteList";
import styles from "./SitesDashboard.module.scss";

export type SiteWithStats = Site & {
  stats: Stats;
  isStatsLoading?: boolean;
};
interface SitesDashboardProps {
  sites: SiteWithStats[];
  sitesIsPenging: boolean;
  pagination: PaginationConfig;
  setPagination: (pagination: PaginationConfig) => void;
  setSearchTerm: (searchTerm: string) => void;
  searchTerm: string;
  authorizations?: Authorizations;
  noResultsForSearch: boolean;
  // Shown right under the list (e.g. the Autopto launch checklist).
  footer?: ReactNode;
}

export const SitesDashboard = ({
  sites,
  sitesIsPenging,
  setPagination,
  pagination,
  searchTerm,
  setSearchTerm,
  authorizations,
  noResultsForSearch,
  footer,
}: SitesDashboardProps) => {
  const navigate = useNavigate();

  const addSite = () => {
    localStorage.removeItem("siteId");
    navigate(routes.site.wizard({ tab: "new" }));
  };

  const hasCreatePermission = authorizations?.collection?.includes("create");

  return (
    <div className={styles.page}>
      <main className={styles.dashboard}>
        <div className={styles.dashboardActions}>
          <SearchBar onChange={setSearchTerm} value={searchTerm} />
          <Button onClick={addSite} disabled={!hasCreatePermission}>
            <span className={styles.buttonText}>
              <Icon iconName="PlusIcon" /> Add Site
            </span>
          </Button>
        </div>

        {noResultsForSearch ? (
          <NoData
            title="No sites match your search"
            description={`No results for “${searchTerm}”. Try changing or clearing the filter.`}
          />
        ) : (
          <>
            <SiteList
              sites={sites}
              sitesIsPenging={sitesIsPenging}
              authorizations={authorizations}
            />
            <Pagination
              {...pagination}
              isLoading={sitesIsPenging}
              totalItems={pagination.totalItems}
              onPageChange={(page) => {
                setPagination({ ...pagination, currentPage: page });
              }}
              onItemsPerPageChange={(items) => {
                setPagination({ ...pagination, itemsPerPage: items });
              }}
            />
          </>
        )}
        {footer}
      </main>
    </div>
  );
};
